/**
 * postAuthRoute.js — Decide where an authenticated user should land right
 * after login / registration.
 *
 * Rules (in priority order):
 *   1. Admin  → /admin
 *   2. User has ≥1 MyEvent (ORGANIZER / CO_ORGANIZER) → /myevent/{firstId}
 *   3. User is "NEXT-era" (created_at ≥ NEXT_LAUNCH_DATE) with 0 event → /onboarding-myevent
 *   4. Otherwise (historical legacy client) → /espace-client
 *
 * Design constraints:
 *   - Historical clients (created BEFORE NEXT_LAUNCH_DATE) with no
 *     event_members MUST keep landing on /espace-client. No exception.
 *   - Never mutate user data during resolution — read-only.
 */
import { api } from "@/lib/api";

// Fixed production switchover date. Any user account created STRICTLY
// before this instant is considered a "historical legacy client" and
// keeps its /espace-client access untouched.
// Do NOT compute this dynamically — it must be stable across redeploys.
export const NEXT_LAUNCH_DATE = "2026-08-12T00:00:00+00:00";

const NEXT_LAUNCH_TS = Date.parse(NEXT_LAUNCH_DATE);

function isNextEraUser(user) {
  if (!user?.created_at) return false;
  const ts = Date.parse(user.created_at);
  if (Number.isNaN(ts)) return false;
  return ts >= NEXT_LAUNCH_TS;
}

/**
 * Resolve the correct post-auth destination for `user`.
 * Returns a react-router path string.
 *
 * @param {object} user  Authenticated user object from /api/auth/me
 * @param {object} [opts]
 * @param {string} [opts.explicitFrom]  A "?from=…" preserved by ProtectedRoute
 *                                      that should override defaults when safe.
 */
export async function resolvePostAuthRoute(user, opts = {}) {
  // 1. Admin always wins
  if (user?.role === "admin") return "/admin";

  // 2. Honour an explicit deep-link redirect from ProtectedRoute, but ONLY
  //    if it's a real internal path (never external, never login/register).
  const explicit = opts.explicitFrom;
  if (
    typeof explicit === "string"
    && explicit.startsWith("/")
    && !explicit.startsWith("//")
    && explicit !== "/connexion"
    && explicit !== "/inscription"
    && explicit !== "/espace-client"
  ) {
    return explicit;
  }

  // 3. Ask the backend for the user's MyEvents (ORGANIZER / CO_ORGANIZER)
  let events = [];
  try {
    const { data } = await api.get("/next/my-events");
    events = Array.isArray(data?.events) ? data.events : [];
  } catch {
    // If the endpoint is unreachable, fall back to the safest default so
    // no client is ever locked out of their existing dashboard.
    return "/espace-client";
  }

  if (events.length > 0) {
    return `/myevent/${events[0].id}`;
  }

  // 4. No event — split by era to protect historical clients.
  if (isNextEraUser(user)) {
    return "/onboarding-myevent";
  }
  return "/espace-client";
}
